import { useOrderMovements } from '@api/useOrderMovements'
import { Spin } from 'antd'
import type React from 'react'
import { useEffect, useMemo, useState } from 'react'

import { BolForm } from './BOL/BolForm'
import { DropForm } from './Drops/DropForm'
import { BypassDisplay } from './ErrorDisplay/BypassDisplay'
import { ErrorControl } from './ErrorDisplay/ErrorControl'
import { BolImageModal } from './BolImageModal'
import { DropAttachmentModal } from './DropAttachmentModal'
import { useSecurityContext } from '@/components/Navigation/SecurityContext'

import type { DeliveryTicketModalProps } from './Drops/DeliveryTicketModal'
import type { GridApi } from 'ag-grid-community'

type MovementDetailProps = {
  data: Gravitate.OrderMovement
  setShowingTicketModal: (show: boolean) => void
  setDeliveryTicketData: (data: DeliveryTicketModalProps['data']) => void
  gridAPIRef?: React.MutableRefObject<GridApi>
}

export function MovementDetail({
  data,
  setShowingTicketModal,
  setDeliveryTicketData,
  gridAPIRef,
}: MovementDetailProps) {
  const [hasBeenSaved, setHasBeenSaved] = useState({ bols: false, drops: false })
  const { useOrderMovementDetailQuery } = useOrderMovements()
  const { data: movementDetails, isFetching } = useOrderMovementDetailQuery(data.order_id)
  const showBypass = useMemo(() => !!data?.validation_bypass, [data])
  const showError = useMemo(() => data?.movement_validation_issues?.length > 0 && !showBypass, [data])
  const [editingBols, setEditingBols] = useState(!hasBeenSaved.bols)
  const [editingDrops, setEditingDrops] = useState(!hasBeenSaved.drops)

  const [showingImageModal, setShowingImageModal] = useState(false)
  const [showingDropAttachmentModal, setShowingDropAttachmentModal] = useState(false)

  const { scopes } = useSecurityContext()
  const canEditOrderMovements = scopes.order_movements?.edit

  const [selectedDetail, setSelectedDetail] = useState<Gravitate.OrderMovementDetails | undefined>(movementDetails)

  useEffect(() => {
    setSelectedDetail(movementDetails)
  }, [movementDetails])

  useEffect(() => {
    let bolSaved = false
    let dropSaved = false
    if (movementDetails) {
      if (movementDetails.bols) {
        bolSaved = true
      }
      if (movementDetails.store_drops) {
        dropSaved = true
      }
      setHasBeenSaved({ bols: bolSaved, drops: dropSaved })
    }
  }, [movementDetails])

  const onBOLSuccess = (updatedData: Gravitate.OrderMovementDetails['bols']) => {
    setHasBeenSaved((prev) => ({ ...prev, bols: true }))
    if (hasBeenSaved.drops && !editingDrops) {
      const rowNode = gridAPIRef?.current?.getRowNode(data?.id ?? '')
      rowNode?.setData(updatedData)
    }
  }

  const onDropSuccess = (updatedData: Gravitate.OrderMovementDetails['store_drops']) => {
    setHasBeenSaved((prev) => ({ ...prev, drops: true }))

    if (hasBeenSaved.bols && !editingBols) {
      const rowNode = gridAPIRef?.current?.getRowNode(data?.id ?? '')
      rowNode?.setData(updatedData)
    }
  }

  if (isFetching) {
    return (
      <div className='center-flex-container p-5'>
        <Spin />
      </div>
    )
  }
  const showDropForm = !!selectedDetail && data.type?.toLowerCase() !== 'backhaul'

  function handleChangeDropAttachments(updatedImages: Gravitate.DeliveryAttachment[], storeNumber: string) {
    if (!movementDetails) return
    const updatedAttachments = [...(selectedDetail?.delivery_attachments || [])]

    // Find existing attachment object that contains this store, or create new one
    let attachmentObjIndex = updatedAttachments.findIndex((obj) => storeNumber in obj)

    if (attachmentObjIndex === -1) {
      // No existing object has this store, create a new one
      attachmentObjIndex = updatedAttachments.length
      updatedAttachments.push({})
    }

    // Update the images for this store
    updatedAttachments[attachmentObjIndex][storeNumber] = updatedImages
    setSelectedDetail({
      ...movementDetails,
      delivery_attachments: updatedAttachments,
    })
  }

  function handleChangeBOLImages(updatedImages: Gravitate.Photo[], index: number) {
    if (!movementDetails) return
    const updatedBols = [...(selectedDetail?.bols || [])]
    updatedBols[index].photos = updatedImages
    setSelectedDetail({
      ...movementDetails,
      bols: updatedBols,
    })
  }

  return (
    <>
      <div>
        {showBypass && <BypassDisplay order={data} />}
        {showError && <ErrorControl gridAPIRef={gridAPIRef} order={data} />}
        {!!selectedDetail && (
          <BolForm
            editing={editingBols}
            setEditing={setEditingBols}
            editingDrops={editingDrops}
            onBOLSuccess={onBOLSuccess}
            hasBeenSaved={hasBeenSaved}
            orderId={data.order_id}
            isBackhaul={data.type?.toLowerCase() === 'backhaul'}
            detail={movementDetails}
            handleOpenImageModal={setShowingImageModal}
            timezone={data?.bol_date_local}
          />
        )}
        {showDropForm && (
          <DropForm
            onDropSuccess={onDropSuccess}
            hasBeenSaved={hasBeenSaved}
            editingDrops={editingDrops}
            setEditing={setEditingDrops}
            editingBols={editingBols}
            order={data}
            detail={movementDetails}
            setDeliveryTicketData={setDeliveryTicketData}
            handleOpenDropAttachmentsModal={setShowingDropAttachmentModal}
            setShowingTicketModal={setShowingTicketModal}
            timezone={data?.delivery_date_local}
          />
        )}
      </div>
      <BolImageModal
        visible={showingImageModal}
        setShowingModal={setShowingImageModal}
        canEditOrderMovements={!!canEditOrderMovements}
        movementDetails={movementDetails}
        onChangeImages={handleChangeBOLImages}
      />
      <DropAttachmentModal
        visible={showingDropAttachmentModal}
        setShowingModal={setShowingDropAttachmentModal}
        movementDetails={movementDetails}
        canEditOrderMovements={!!canEditOrderMovements}
        onChangeImages={handleChangeDropAttachments}
      />
    </>
  )
}
